"use client";

import { AnimatedBanner } from "@faire/deprecated-retailer-visitor-shared/components/AnimatedBanner/AnimatedBanner";
import { getPromoBanner as getBanner } from "@faire/deprecated-retailer-visitor-shared/components/Notifications/PromoBanner/__internal__/getPromoBanner";
import { MixedPromoBanners } from "@faire/deprecated-retailer-visitor-shared/components/Notifications/PromoBanner/__internal__/MixedPromoBanners";
import { useSiteBannersList } from "@faire/deprecated-retailer-visitor-shared/components/Notifications/PromoBanner/__internal__/useSiteBannersList";
import { PromoBannerType } from "@faire/deprecated-retailer-visitor-shared/components/Notifications/PromoBanner/bannerOrder";
import { usePOSSummary } from "@faire/deprecated-retailer-visitor-shared/features/PointOfSale/hooks/usePOSSummary";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { useIsLoggedOutLightGateTreatmentWithEligibility } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingLoggedOutLightGate";
import { useMarketStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/Events/FaireMarkets/MarketStore";
import { useRecordMilestoneByDay } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useMilestone";
import { useTopOfPageContextStore } from "@faire/deprecated-retailer-visitor-shared/stores/ui/TopOfPageContextStore";
import { useLoggedOutPromoBanners } from "@faire/deprecated-retailer-visitor-shared/stores/ui/useLoggedOutPromoBanners";
import { usePromoBanners } from "@faire/deprecated-retailer-visitor-shared/stores/ui/usePromoBanners";
import { Decorative } from "@faire/design-tokens";
import { HAS_SEEN_REFERRAL_REENGAGEMENT_BANNER } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/HAS_SEEN_REFERRAL_REENGAGEMENT_BANNER";
import { route as posHomeRoute } from "@faire/web-api--source/routes/www/point-of-sale";
import { route as posAboutRoute } from "@faire/web-api--source/routes/www/point-of-sale/about";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useIntersectionObserver } from "@faire/web/ui/hooks/useIntersectionObserver";
import { useMergedRefs } from "@faire/web/ui/hooks/useMergedRefs";
import { removeLocaleFromUrl } from "@faire/web/ui/routing/util";
import compact from "lodash/compact";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { matchPath } from "react-router-dom";
import styled, { css } from "styled-components";

export const PromoBanner = () => {
  const siteBannersList = useSiteBannersList();
  const bannerRef = React.useRef<HTMLDivElement>(null);
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const [{ bannerHeight }, setStore] = useTopOfPageContextStore([
    "bannerHeight",
  ]);

  const isLightGatingTreatment =
    useIsLoggedOutLightGateTreatmentWithEligibility();

  const { pathname: pathnameWithMaybeLocale } = useLocation();

  const { hasLinkedPOS } = usePOSSummary();

  const pathname = useMemo(
    () => removeLocaleFromUrl(pathnameWithMaybeLocale),
    [pathnameWithMaybeLocale]
  );

  const nonStickyHeader: boolean = useMemo(() => {
    const nonStickyHeaderPath = [
      {
        path: posAboutRoute,
        exact: true,
      },
      ...(!hasLinkedPOS
        ? [
            {
              path: posHomeRoute,
              exact: true,
            },
          ]
        : []),
    ].filter(isNotUndefined);
    return nonStickyHeaderPath.some(
      (path) => matchPath(pathname, path) !== null
    );
  }, [pathname, hasLinkedPOS]);

  const { isShowingCountdownBanner: isShowingMarketCountdownBanner } =
    useMarketStore();
  useEffect(() => {
    if (bannerRef.current != null) {
      if (bannerHeight !== bannerRef.current.clientHeight) {
        setStore({ bannerHeight: bannerRef.current.clientHeight });
      }
    }
  });

  const recordMilestoneByDay = useRecordMilestoneByDay();
  const { banners } = usePromoBanners();
  useEffect(() => {
    return () => {
      if (banners.includes(PromoBannerType.REFERRAL_FOLLOWUP)) {
        recordMilestoneByDay(HAS_SEEN_REFERRAL_REENGAGEMENT_BANNER);
      }
    };
  }, [banners, recordMilestoneByDay]);

  const loggedOutPromoBanners = useLoggedOutPromoBanners();

  const getBannerWithKeyProp = useCallback(
    (type: PromoBannerType, index: number): React.ReactNode => {
      const banner = getBanner(type);
      return banner ? (
        <React.Fragment key={index}>{getBanner(type)}</React.Fragment>
      ) : null;
    },
    []
  );

  const bannersWithKeyProp = useCallback(
    (bannerList: PromoBannerType[]) => {
      return compact(bannerList.map(getBannerWithKeyProp));
    },
    [getBannerWithKeyProp]
  );

  const renderBanners = useCallback(() => {
    const availableBanners = isLoggedInRetailer
      ? banners
      : loggedOutPromoBanners;

    if (availableBanners.length === 0) {
      return null;
    }

    // Banners that do not need special styling
    if (
      availableBanners[0] === PromoBannerType.FAIRE_VALUES ||
      availableBanners[0] === PromoBannerType.MARKET_COUNTDOWN ||
      availableBanners[0] === PromoBannerType.BRAND_PREVIEW
    ) {
      return bannersWithKeyProp(availableBanners);
    }

    if (siteBannersList.length > 0) {
      return <MixedPromoBanners banners={siteBannersList} />;
    }

    return (
      <StyledAnimatedBanner
        banners={bannersWithKeyProp(availableBanners)}
        bannerNames={availableBanners}
      />
    );
  }, [
    banners,
    bannersWithKeyProp,
    isLoggedInRetailer,
    loggedOutPromoBanners,
    siteBannersList,
  ]);

  const bannerViewRef = useIntersectionObserver((entry) => {
    if (entry.isIntersecting) {
      setStore({ isBannerInView: true });
    } else {
      setStore({ isBannerInView: false });
    }
  });

  useEffect(() => {
    return () => {
      setStore({ isBannerInView: false });
    };
  }, [setStore]);

  const mergedRef = useMergedRefs(bannerRef, bannerViewRef);

  return useMemo(
    () => (
      <BannerWrapper
        ref={mergedRef}
        $isSticky={!nonStickyHeader && isShowingMarketCountdownBanner}
        $isLightGatingTreatment={isLightGatingTreatment}
      >
        {renderBanners()}
      </BannerWrapper>
    ),
    [
      mergedRef,
      nonStickyHeader,
      isShowingMarketCountdownBanner,
      isLightGatingTreatment,
      renderBanners,
    ]
  );
};

const StyledAnimatedBanner = styled(AnimatedBanner)`
  background-color: ${Decorative.tint.neutral};
`;

const BannerWrapper = styled.div<{
  $isSticky?: boolean;
  $isLightGatingTreatment?: boolean;
}>`
  z-index: ${({ $isLightGatingTreatment }) =>
    $isLightGatingTreatment ? 12 : 2};
  position: relative;
  ${({ $isSticky }) =>
    $isSticky
      ? css`
          ${responsiveMediaQueries.desktopAndAbove} {
            position: sticky;
            top: 0;
            z-index: 13;
          }
        `
      : null};
`;
